import axios from "../configs/axios.config";

const SERVER = import.meta.env.VITE_API_URL;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("prima_session");
  if (!token) {
    throw new Error("Token not found");
  }
  return {
    Authorization: `Bearer ${token.replace(/^Bearer\s+/i, "").replace(/['"]/g, "")}`,
  };
};

// ---------------- FETCH AUDIT LOGS ----------------
export const fetchAuditLogs = async (params = {}) => {
  try {
    const response = await axios.get(`${SERVER}/auditlogs`, {
      headers: getAuthHeaders(),
      params: {
        page: params.page || 1,
        limit: params.limit || 50,
        module: params.module,
        actionType: params.actionType,
        performedBy: params.performedBy,
        resourceType: params.resourceType,
        startDate: params.startDate,
        endDate: params.endDate,
        search: params.search,
        sortBy: params.sortBy || "createdAt",
        sortOrder: params.sortOrder || "desc",
      },
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.error(
      "Error fetching audit logs:",
      error.response?.data?.message || error.message
    );
    throw new Error(error.response?.data?.message || "Failed to fetch audit logs");
  }
};

// ---------------- FETCH SINGLE AUDIT LOG ----------------
export const fetchAuditLogById = async (id) => {
  if (!id) throw new Error("Audit log ID not provided");

  try {
    const response = await axios.get(`${SERVER}/auditlogs/${id}`, {
      headers: getAuthHeaders(),
      withCredentials: true,
    });

    return response.data?.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch audit log");
  }
};

// ---------------- FETCH AUDIT STATS ----------------
export const fetchAuditStats = async (params = {}) => {
  try {
    const response = await axios.get(`${SERVER}/auditlogs/stats`, {
      headers: getAuthHeaders(),
      params: {
        startDate: params.startDate,
        endDate: params.endDate,
      },
      withCredentials: true,
    });

    return response.data?.data;
  } catch (error) {
    console.error(
      "Error fetching audit stats:",
      error.response?.data?.message || error.message
    );
    throw new Error(error.response?.data?.message || "Failed to fetch audit stats");
  }
};

// ---------------- FETCH LOGS BY ADMIN ----------------
export const fetchAuditLogsByAdmin = async (adminId, params = {}) => {
  if (!adminId) throw new Error("Admin ID not provided");

  try {
    const response = await axios.get(`${SERVER}/auditlogs/admin/${adminId}`, {
      headers: getAuthHeaders(),
      params: {
        page: params.page || 1,
        limit: params.limit || 50,
      },
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch audit logs");
  }
};

// ---------------- FETCH LOGS BY RESOURCE ----------------
export const fetchAuditLogsByResource = async (resourceType, resourceId, params = {}) => {
  if (!resourceType || !resourceId) {
    throw new Error("Resource type and ID are required");
  }

  try {
    const response = await axios.get(
      `${SERVER}/auditlogs/resource/${resourceType}/${resourceId}`,
      {
        headers: getAuthHeaders(),
        params: {
          page: params.page || 1,
          limit: params.limit || 50,
        },
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch audit logs");
  }
};

// ---------------- EXPORT AUDIT LOGS ----------------
export const exportAuditLogs = async (params = {}, format = "json") => {
  try {
    const response = await axios.get(`${SERVER}/auditlogs/export`, {
      headers: getAuthHeaders(),
      params: {
        module: params.module,
        actionType: params.actionType,
        performedBy: params.performedBy,
        startDate: params.startDate,
        endDate: params.endDate,
        format,
      },
      withCredentials: true,
      ...(format === "csv" ? { responseType: "blob" } : {}),
    });

    if (format === "csv") {
      // Trigger download for CSV
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `audit_logs_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    }

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to export audit logs");
  }
};

export default {
  fetchAuditLogs,
  fetchAuditLogById,
  fetchAuditStats,
  fetchAuditLogsByAdmin,
  fetchAuditLogsByResource,
  exportAuditLogs,
};
